import dayjs, { Dayjs } from 'dayjs';

export const SCHEDULE_LIST_MIN_DATE = '2000-01-01';
export const SCHEDULE_LIST_MAX_DATE = '2100-12-31';
export const SCHEDULE_LIST_MAX_RANGE_LABEL = '1년';

export type ScheduleListRangeErrorCode =
    | 'required'
    | 'format'
    | 'year'
    | 'order'
    | 'tooLong';

export type ScheduleListRangeValidation = {
    isValid: boolean;
    errorCode?: ScheduleListRangeErrorCode;
    errorMessage: string;
    dayCount: number;
    maxEndDate?: string;
};

const STRICT_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseStrictScheduleDate = (value: string): Dayjs | null => {
    if (!STRICT_DATE_PATTERN.test(value)) return null;

    const parsed = dayjs(`${value}T00:00:00`);
    if (!parsed.isValid() || parsed.format('YYYY-MM-DD') !== value) return null;
    return parsed;
};

const isWithinSupportedYears = (date: Dayjs) => date.year() >= 2000 && date.year() <= 2100;

export const getScheduleListMaxEndDate = (startDate: string) => {
    const parsedStart = parseStrictScheduleDate(startDate);
    if (!parsedStart || !isWithinSupportedYears(parsedStart)) return SCHEDULE_LIST_MAX_DATE;

    const oneYearLater = parsedStart.add(1, 'year');
    return oneYearLater.isAfter(dayjs(SCHEDULE_LIST_MAX_DATE), 'day')
        ? SCHEDULE_LIST_MAX_DATE
        : oneYearLater.format('YYYY-MM-DD');
};

export const validateScheduleListRange = (
    startDate: string,
    endDate: string
): ScheduleListRangeValidation => {
    if (!startDate || !endDate) {
        return {
            isValid: false,
            errorCode: 'required',
            errorMessage: '시작일과 종료일을 모두 입력해주세요.',
            dayCount: 0,
        };
    }

    const parsedStart = parseStrictScheduleDate(startDate);
    const parsedEnd = parseStrictScheduleDate(endDate);
    if (!parsedStart || !parsedEnd) {
        return {
            isValid: false,
            errorCode: 'format',
            errorMessage: '날짜를 YYYY-MM-DD 형식의 실제 날짜로 입력해주세요.',
            dayCount: 0,
        };
    }

    if (!isWithinSupportedYears(parsedStart) || !isWithinSupportedYears(parsedEnd)) {
        return {
            isValid: false,
            errorCode: 'year',
            errorMessage: '조회 가능 연도는 2000년부터 2100년까지입니다.',
            dayCount: 0,
        };
    }

    const maxEndDate = getScheduleListMaxEndDate(startDate);
    if (parsedStart.isAfter(parsedEnd, 'day')) {
        return {
            isValid: false,
            errorCode: 'order',
            errorMessage: '종료일은 시작일과 같거나 이후여야 합니다.',
            dayCount: 0,
            maxEndDate,
        };
    }

    if (parsedEnd.isAfter(dayjs(maxEndDate), 'day')) {
        return {
            isValid: false,
            errorCode: 'tooLong',
            errorMessage: `조회 기간은 최대 ${SCHEDULE_LIST_MAX_RANGE_LABEL}입니다. 시작일 기준 다음 해 같은 날짜(${maxEndDate})까지 선택할 수 있습니다.`,
            dayCount: parsedEnd.diff(parsedStart, 'day') + 1,
            maxEndDate,
        };
    }

    return {
        isValid: true,
        errorMessage: '',
        dayCount: parsedEnd.diff(parsedStart, 'day') + 1,
        maxEndDate,
    };
};

export const isLongScheduleListRange = (startDate: string, endDate: string) => {
    const validation = validateScheduleListRange(startDate, endDate);
    if (!validation.isValid) return false;

    const parsedStart = parseStrictScheduleDate(startDate)!;
    const parsedEnd = parseStrictScheduleDate(endDate)!;
    return !parsedEnd.isBefore(parsedStart.add(6, 'month'), 'day');
};
